/**
 * Notepad Editor State Hook
 *
 * Manages the editor mode inside the Notepad drawer.
 * Tracks dirty state for unsaved changes confirmation.
 */

import { useState, useCallback, createContext, useContext } from 'react';

export type NotepadEditorMode = 'list' | 'create' | 'edit';
export type NotepadEntityType = 'task' | 'note';

export interface NotepadEditorState {
  mode: NotepadEditorMode;
  entityType: NotepadEntityType | null;
  entityId: string | null;
  isDirty: boolean;
}

export interface NotepadEditorActions {
  openCreate: (type: NotepadEntityType) => void;
  openEdit: (type: NotepadEntityType, id: string) => void;
  backToList: () => void;
  reset: () => void;
  setDirty: (dirty: boolean) => void;
}

export interface UseNotepadEditorStateReturn extends NotepadEditorState, NotepadEditorActions {}

const initialState: NotepadEditorState = {
  mode: 'list',
  entityType: null,
  entityId: null,
  isDirty: false,
};

/**
 * Hook to manage notepad editor state
 */
export function useNotepadEditorState(): UseNotepadEditorStateReturn {
  const [state, setState] = useState<NotepadEditorState>(initialState);

  const openCreate = useCallback((type: NotepadEntityType) => {
    setState({
      mode: 'create',
      entityType: type,
      entityId: null,
      isDirty: false,
    });
  }, []);

  const openEdit = useCallback((type: NotepadEntityType, id: string) => {
    setState({
      mode: 'edit',
      entityType: type,
      entityId: id,
      isDirty: false,
    });
  }, []);

  const backToList = useCallback(() => {
    setState(initialState);
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const setDirty = useCallback((dirty: boolean) => {
    setState((prev) => ({ ...prev, isDirty: dirty }));
  }, []);

  return {
    ...state,
    openCreate,
    openEdit,
    backToList,
    reset,
    setDirty,
  };
}

/**
 * Context for sharing editor state across notepad components
 * Avoids deep prop drilling from drawer → tabs → cards
 */
export type NotepadEditorContextValue = UseNotepadEditorStateReturn;

export const NotepadEditorContext = createContext<NotepadEditorContextValue | null>(null);

/**
 * Hook to access notepad editor context
 * Must be used within NotepadEditorContext.Provider
 */
export function useNotepadEditor(): NotepadEditorContextValue {
  const context = useContext(NotepadEditorContext);
  if (!context) {
    throw new Error('useNotepadEditor must be used within NotepadEditorContext.Provider');
  }
  return context;
}
